import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Textarea,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'

import { HttpStatus } from 'enums'
import { STATUS_ENUM } from 'types'
import { RequestPortfolioSupportRequestDto } from 'types/portfolioTypes'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import { DropdownOptionString } from 'components/partials/DropdownWithTags'
import { ERROR_MESSAGE_1 } from 'constants/content.constants'
import { CustomerSupportType } from 'constants/general.constants'
import { PortfolioSupportCategory } from 'constants/portfolio.constants'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSDropdown from 'designSystem/components/PDSDropdown/PDSDropdown'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'

type Category = DropdownOptionString<PortfolioSupportCategory>

const categories: Category[] = [
  {
    label: 'Suggested Trades',
    value: PortfolioSupportCategory.SUGGESTED_TRADES,
  },
  {
    label: 'Unsupported Assets',
    value: PortfolioSupportCategory.UNSUPPORTED_ASSETS,
  },
  {
    label: 'Trade Timing',
    value: PortfolioSupportCategory.TRADE_TIMING,
  },
  {
    label: 'Other',
    value: PortfolioSupportCategory.OTHER,
  },
  {
    label: 'Complaint',
    value: PortfolioSupportCategory.COMPLAINT,
  },
]

const additionalDetails: Record<PortfolioSupportCategory, string[]> = {
  [PortfolioSupportCategory.UNSUPPORTED_ASSETS]: [
    'There are certain assets Pave does not have requisite data to make decisions on. These assets are referred to as unsupported. When an asset is unsupported, it cannot be include in portfolio optimizations. As a result, it is recommended that these assets be sold out of. Unfortunately, in most cases, Pave Pro cannot trade these assets meaning advisors need to sell out of them manually.',
    'There are certain assets that Pave can support if requested. Most stocks and ETFs can be supported by Pave, but mutual funds and money market funds cannot. If you would like to request that an asset in this portfolio be supported, please make note of it in the description below.',
  ],
  [PortfolioSupportCategory.SUGGESTED_TRADES]: [],
  [PortfolioSupportCategory.OTHER]: [],
  [PortfolioSupportCategory.COMPLAINT]: [],
  [PortfolioSupportCategory.TRADE_TIMING]: [
    'Your next suggested trades are scheduled to be generated at nextSuggestionsDate',
  ],
}

type SupportRequestForm = {
  category: PortfolioSupportCategory | undefined
  description: string | undefined
}

type Props = {
  daysUntilSuggestions: number
  entryPoint: string
  isOpen: boolean
  onClose: () => void
  portfolioId?: string
  incompletePortfolio: boolean
}
function RequestSupportModal({
  daysUntilSuggestions,
  entryPoint,
  isOpen,
  onClose,
  portfolioId,
  incompletePortfolio,
}: Props) {
  const [requestPortfolioSupport, { isLoading }] =
    paveApi.useRequestPortfolioSupportMutation()
  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()
  const nextSuggestionsDate = new Date()
  nextSuggestionsDate.setDate(
    nextSuggestionsDate.getDate() + daysUntilSuggestions
  )

  const handleSubmit = async (values: SupportRequestForm) => {
    try {
      const { category, description } = values

      if (!category || !description || !portfolioId) {
        throw new Error()
      }

      const data: RequestPortfolioSupportRequestDto = {
        portfolioId,
        body: {
          message: description,
          category,
          entryPoint,
          customerSupportType:
            category === PortfolioSupportCategory.COMPLAINT
              ? CustomerSupportType.COMPLAINT
              : CustomerSupportType.SUPPORT,
        },
      }

      const response = await requestPortfolioSupport(data).unwrap()

      if (response?.status === STATUS_ENUM.SUCCESS) {
        showSuccessToast(
          'Support request submitted',
          'Your support request was submitted successfully. A member of our team will reach out shortly via email.'
        )
        onClose()
      } else {
        throw new Error()
      }
    } catch (error) {
      if (
        error !== null &&
        typeof error === 'object' &&
        'status' in error &&
        (error as { status: number }).status === HttpStatus.FORBIDDEN
      ) {
        showViewOnlyDefaultToast()
      } else {
        showErrorToast(ERROR_MESSAGE_1)
      }
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody paddingX={0}>
            <Formik<SupportRequestForm>
              initialValues={{ category: undefined, description: undefined }}
              onSubmit={async (values, { setSubmitting }) => {
                await handleSubmit(values)
                setSubmitting(false)
              }}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form>
                  <PDSFlexBox
                    flexDirection="column"
                    gap={SIZE.s16}
                    paddingX={SIZE.s32}
                    paddingBottom={SIZE.s16}
                  >
                    <PDSText
                      size="md"
                      color={COLOR.textPrimary}
                      fontWeight={FONT_WEIGHT.semibold}
                    >
                      Request support from Pave.
                    </PDSText>
                    <PDSText
                      size="sm"
                      color={COLOR.textSecondary}
                      fontWeight={FONT_WEIGHT.medium}
                    >
                      Please choose the category that best fits your inquiry and
                      describe it below so we can connect you to the right
                      member of our team.
                    </PDSText>

                    <PDSDropdown
                      label="Support Category"
                      placeholder="Select a category"
                      options={categories}
                      onChange={(selectedOption: any) =>
                        setFieldValue('category', `${selectedOption?.value}`)
                      }
                      value={values.category}
                      menuShouldBlockScroll={false}
                    />

                    {values.category && (
                      <>
                        {additionalDetails[values.category].map(
                          (item, index) => {
                            if (
                              incompletePortfolio &&
                              values.category ===
                                PortfolioSupportCategory.TRADE_TIMING
                            ) {
                              return <></>
                            }

                            const text = item.replace(
                              'nextSuggestionsDate',
                              nextSuggestionsDate.toDateString()
                            )

                            return (
                              <PDSText
                                color={COLOR.textTertiary}
                                fontWeight={FONT_WEIGHT.regular}
                                key={index}
                                size="sm"
                              >
                                {text}
                              </PDSText>
                            )
                          }
                        )}
                      </>
                    )}

                    <PDSFlexBox flexDirection="column" gap={SIZE.s4}>
                      <PDSText
                        color={COLOR.textSecondary}
                        size="md"
                        fontWeight={FONT_WEIGHT.medium}
                      >
                        Anything else we should know?{' '}
                        <PDSText
                          color={COLOR.textTertiary}
                          size="md"
                          fontWeight={FONT_WEIGHT.medium}
                        >
                          (Optional)
                        </PDSText>
                      </PDSText>
                      {/* TODO: Create PDSTextarea */}
                      <Textarea
                        placeholder="Please enter any additional information here."
                        value={values.description}
                        onChange={(e) =>
                          setFieldValue('description', e.target.value)
                        }
                      />
                    </PDSFlexBox>
                  </PDSFlexBox>

                  <PDSFlexBox
                    flexDirection="column"
                    borderTop={`1px solid ${COLOR.content_border}`}
                  >
                    <PDSFlexBox
                      flexDirection="column"
                      padding={SIZE.s32}
                      gap={SIZE.s8}
                    >
                      <PDSButton
                        text="Submit Request"
                        type="submit"
                        disabled={isLoading || isSubmitting || !values.category}
                      />
                      <PDSButton text="Cancel" variant="tertiary" />
                    </PDSFlexBox>
                  </PDSFlexBox>
                </Form>
              )}
            </Formik>
          </ModalBody>
          {isLoading && <ModalLoader />}
        </ModalContent>
      </Modal>
    </>
  )
}

export default RequestSupportModal
